import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import type { ApiError } from '@/lib/api/error';
import { handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { queryClient } from '@/lib/queryClient';
import { deleteDraftCatalog, getDraftCatalog, getDraftCatalogHeader, saveDraftCatalog, getDraftCatalogFilePresignedUrl, completeDraftCatalogFileUpload } from './api';
import { draftCatalogKey } from './key';
import type { SaveDraftCatalogRequest, CompleteDraftCatalogFileUploadRequest } from './type';
const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

// 型と定数の再エクスポート
export type { GetDraftCatalogHeaderResponse, GetDraftCatalogResponse, SaveDraftCatalogRequest, DeviceCatalogSection, GetDraftCatalogFilePresignedUrlResponse, CompleteDraftCatalogFileUploadRequest } from './type';
export type DeviceCatalogAttribute = import('./type').DeviceCatalogSection['attributes'][number];

/**
 * Draft デバイスカタログ情報のデータをプリフェッチするローダー
 *
 * 各タブページのloaderで使用します。
 * @example
 * ```ts
 * export const Route = createFileRoute(...)({
 *   loader: prefetchDraftCatalog,
 * });
 * ```
 */
export async function prefetchDraftCatalog({
  params
}: {
  params: {
    id: string;
  };
}) {
  await queryClient.ensureQueryData({
    queryKey: draftCatalogKey.detail(params.id),
    queryFn: () => getDraftCatalog(params.id),
    staleTime: FIVE_MINUTES_IN_MS
  });
}

/**
 * Draft デバイスカタログ情報取得
 */
export function useGetDraftCatalog(id: string) {
  return useQuery({
    queryKey: draftCatalogKey.detail(id),
    queryFn: () => getDraftCatalog(id),
    staleTime: FIVE_MINUTES_IN_MS,
    refetchOnWindowFocus: false
  });
}

/**
 * Draft デバイスカタログ ヘッダー情報取得
 */
export function useGetDraftCatalogHeader(id: string) {
  return useQuery({
    queryKey: draftCatalogKey.header(id),
    queryFn: () => getDraftCatalogHeader(id),
    staleTime: FIVE_MINUTES_IN_MS,
    refetchOnWindowFocus: false
  });
}

/**
 * Draft デバイスカタログ情報更新
 *
 * @param id - カタログID（呼び出し元のページコンポーネントで useParams から取得してこのフックに渡す）
 * @param options - 成功・失敗時のコールバック
 */
export function useSaveDraftCatalog(id: string, options?: {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) {
  return useMutation({
    mutationFn: async (data: SaveDraftCatalogRequest) => {
      return await saveDraftCatalog(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: draftCatalogKey.detail(id)
      });
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      options?.onError?.(error);
    }
  });
}

/**
 * Draft デバイスカタログを削除
 *
 * @param id - カタログID
 * @param options - 成功・失敗時のコールバック
 */
export function useDeleteDraftCatalog(id: string, options?: {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) {
  const {
    i18n,
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.deviceCatalog.header.deleteDialog'
  });
  return useMutation({
    mutationFn: async () => {
      return await deleteDraftCatalog(id);
    },
    onSuccess: () => {
      // 削除後はクエリキャッシュを削除（リフェッチ不要）
      queryClient.removeQueries({
        queryKey: draftCatalogKey.detail(id)
      });
      queryClient.removeQueries({
        queryKey: draftCatalogKey.header(id)
      });
      toast({
        description: t('successMessage')
      });
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
      options?.onError?.(error);
    }
  });
}

/**
 * Draft カタログファイル Presigned URL取得
 *
 * @param id - カタログID
 */
export function useGetDraftCatalogFilePresignedUrl(id: string) {
  const {
    i18n
  } = useTranslation(defaultNS);
  return useMutation({
    mutationFn: async () => {
      return await getDraftCatalogFilePresignedUrl(id);
    },
    onError: (error: ApiError) => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  });
}

/**
 * Draft カタログファイルアップロード完了通知
 *
 * @param id - カタログID
 */
export function useCompleteDraftCatalogFileUpload(id: string) {
  const {
    i18n
  } = useTranslation(defaultNS);
  return useMutation({
    mutationFn: async (data: CompleteDraftCatalogFileUploadRequest) => {
      return await completeDraftCatalogFileUpload(id, data);
    },
    onSuccess: () => {
      // ヘッダー情報を再取得
      queryClient.invalidateQueries({
        queryKey: draftCatalogKey.header(id)
      });
    },
    onError: (error: ApiError) => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  });
}